import {kmsQueries, SignalStatus} from '@domain/kiln-monitoring'
import {Box, useMediaQuery, useTheme} from '@mui/material'
import Grid from '@mui/material/Grid'
import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'

import {
  mapToActiveSections,
  mapToPositionByPartId,
  mapToSensorReadings,
  mapToSignalToSvgMap
} from '../../mappers'
import {useKiln, useSensors} from '../../providers'
import {countAreaState} from '../../utils'
import {KilnMetricStatus} from '../shared'

import {KilnSvgWrapper} from './kiln-schematic'
import {KilnHeaderMetrics} from './KilnHeaderMetrics'
import {KilnSchematicSkeleton} from './KilnSchematicSkeleton'

export interface KilnSchematicPanelProps {
  hoveredSignal: string | null
  svgClickCb: (localNames: string[]) => void
  onKilnMetricClick: (localNames: string[]) => void
  headerActions?: React.ReactNode
  onAreaClick?: (kilnId: string, area: string) => void
  onCrankClick?: (kilnId: string) => void
  onKilnSwitch?: (kilnId: string) => void
  showMaterialFlow?: boolean
}

export const KilnSchematicPanel = ({
  hoveredSignal,
  svgClickCb,
  onKilnMetricClick,
  headerActions,
  onAreaClick,
  onCrankClick,
  onKilnSwitch,
  showMaterialFlow = true
}: KilnSchematicPanelProps) => {
  const {kilnId, kilnInfo, plantUpmId} = useKiln()
  const {selectedIds, sensorData} = useSensors()
  const {data: sensorMappings, isLoading: mappingsLoading} = useQuery(
    kmsQueries.sensorsMappings(kilnId, plantUpmId)
  )
  const anomaliesQuery = useQuery(kmsQueries.sensorsAnomalies(kilnId, plantUpmId))
  const healthIndicatorsQuery = useQuery(kmsQueries.healthIndicators(kilnId, plantUpmId))
  const theme = useTheme()
  const onlyBigScreen = useMediaQuery(theme.breakpoints.up('sm'))

  const axialBalanceData = healthIndicatorsQuery.data?.axialBalance

  const positionByPartId = useMemo(
    () => mapToPositionByPartId(kilnInfo?.layout?.axialBalanceSignals, axialBalanceData),
    [kilnInfo?.layout?.axialBalanceSignals, axialBalanceData]
  )

  const signalToSvgMap = useMemo(() => mapToSignalToSvgMap(sensorMappings), [sensorMappings])

  const {data: sensorsReadings, isPending: sensorReadingsLoading} = useQuery({
    ...kmsQueries.signals(kilnId, plantUpmId),
    select: mapToSensorReadings
  })
  const areaFreqs = useMemo<Record<string, string>>(
    () => countAreaState(sensorData, sensorsReadings),
    [sensorData, sensorsReadings]
  )

  const activeSections = useMemo(
    () => mapToActiveSections(selectedIds, sensorMappings),
    [selectedIds, sensorMappings]
  )

  const renderSchematic = () =>
    mappingsLoading || healthIndicatorsQuery.isLoading || anomaliesQuery.isLoading ? (
      <KilnSchematicSkeleton />
    ) : sensorMappings ? (
      <Box
        flexGrow={1}
        pr={3}
        sx={{
          // TO PREVENT SVG OVERFLOW
          minHeight: 0,
          minWidth: 0
        }}
        data-test-id="kiln-model"
      >
        <KilnSvgWrapper
          svgClickCb={svgClickCb}
          sensorMappings={sensorMappings}
          anomalies={anomaliesQuery.data}
          activeAreas={activeSections}
          hoveredArea={hoveredSignal ? signalToSvgMap[hoveredSignal] : undefined}
          speedValue={healthIndicatorsQuery.data?.speed?.value}
          isWidget={!showMaterialFlow}
          kilnInfo={kilnInfo}
          rollerMovementByPartId={positionByPartId}
        />
      </Box>
    ) : (
      <></>
    )

  return (
    <Grid flexGrow={1} sx={{maxHeight: '100%'}}>
      <Box display="flex" justifyContent="space-between" gap={2}>
        <KilnHeaderMetrics
          loading={healthIndicatorsQuery.isLoading}
          onKilnMetricClick={onKilnMetricClick}
          healthIndicators={healthIndicatorsQuery.data}
          onCrankClick={onCrankClick}
          onKilnSwitch={onKilnSwitch}
        />
        {headerActions}
      </Box>
      {onlyBigScreen && renderSchematic()}
      {!onlyBigScreen && (
        <Grid
          alignSelf="flex-start"
          color="text.primary"
          direction="row"
          gap={{
            xs: 1,
            sm: 3
          }}
          container
          mb={2}
        >
          {Object.keys(areaFreqs).map((key) => (
            <Grid item xs={5} sm={3} key={key}>
              <KilnMetricStatus
                title={key}
                value={''}
                status={areaFreqs[key] as SignalStatus}
                loading={sensorReadingsLoading}
                onClick={() => onAreaClick?.(kilnId, key)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  )
}
